import { memo, useState, useContext, useMemo, ComponentProps, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tabs, Badge } from 'antd';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { SpuSelect, SpuPreview, UnConfigFooter, ConfigFooter } from './NewSpuSelect';
import {
  FilterBarValueProvider,
  UnConfigSkuItem,
  UnConfigSkuItemProvider,
  ConfigSkuItem,
  SpuClassDirtyContext,
  ClassActiveKnowledgeIdContext,
  SyncCheckboxSelectProvider,
} from './context';
import { useDeferredRender } from './helpers';
import MoreSpuButton from './MoreSpuButton';
import { EditSkuButton } from '@knowledge/knowledges/singleClass/EditSku';
import { countSku } from '@knowledge/common/helpers/sku';
import { useLatest } from 'ahooks';

const UnConfigSkuCount = () => {
  const list = useContext(UnConfigSkuItem);
  const count = useMemo(() => {
    return countSku(list);
  }, [list]);

  return <>{count}</>;
};

const ConfigSkuCount = () => {
  const list = useContext(ConfigSkuItem);
  const count = useMemo(() => {
    return countSku(list);
  }, [list]);

  return <>{count}</>;
};

const SpuArea = memo((props: { citeMode?: boolean; knowledgeName: string }) => {
  const { citeMode, knowledgeName } = props;
  const defered = useDeferredRender();

  return defered ? (
    <div className="flex-grow flex flex-col bg-[#f4f6f8] overflow-hidden h-full">
      <UnConfigSkuItemProvider>
        <SyncCheckboxSelectProvider>
          <SpuAreaInner citeMode={citeMode} knowledgeName={knowledgeName} />
        </SyncCheckboxSelectProvider>
      </UnConfigSkuItemProvider>
    </div>
  ) : null;
});

const UnConfigFooterPortal = (props: ComponentProps<typeof UnConfigFooter>) => {
  const portalDom = document.querySelector('#unConfigFooterPortal'); // 懒得封装了，写死了算了
  return portalDom && props.drawer ? (
    createPortal(<UnConfigFooter {...props} />, portalDom)
  ) : (
    <UnConfigFooter {...props} />
  );
};

const ConfigFooterPortal = (props: ComponentProps<typeof ConfigFooter>) => {
  const portalDom = document.querySelector('#configFooterPortal'); // 懒得封装了，写死了算了
  return portalDom && props.drawer ? (
    createPortal(<ConfigFooter {...props} />, portalDom)
  ) : (
    <ConfigFooter {...props} />
  );
};
export const SpuAreaInner = memo(
  (props: {
    citeMode?: boolean;
    knowledgeName: string;
    drawer?: boolean;
    tabKey?: string;
    onKeyChange?: (key: string) => void;
  }) => {
    const {
      citeMode,
      knowledgeName,
      drawer = false,
      tabKey = citeMode ? 'yet' : 'notyet',
      onKeyChange,
    } = props;
    const { t } = useTranslate();
    const [key, setKey] = useState(tabKey);
    const { value } = useContext(SpuClassDirtyContext);
    const { activeId } = useContext(ClassActiveKnowledgeIdContext);
    const onKeyChangeRef = useLatest(onKeyChange);
    useEffect(() => {
      onKeyChangeRef.current?.(key);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return (
      <Tabs
        activeKey={key}
        onChange={setKey}
        className={css`
          height: 100%;
          display: flex;
          flex-direction: column;
          .ant-tabs-nav {
            flex-shrink: 0;
            padding-left: 16px;
            .ant-tabs-nav-list {
              padding: 5px 0;
            }
          }
          .ant-tabs-content-holder {
            flex-basis: 0;
            overflow: hidden;
            flex-grow: 1;
            .ant-tabs-content {
              height: 100%;
            }
          }
          .ant-tabs-nav-wrap {
            opacity: ${citeMode ? 0 : 1};
          }
        `}
        tabBarExtraContent={{
          right: (
            <>
              {!drawer && !citeMode && <EditSkuButton tabKey={key} />}
              <MoreSpuButton citeMode={citeMode} type="config" knowledgeName={knowledgeName} />
            </>
          ),
        }}
      >
        <Tabs.TabPane
          tab={
            <Badge dot={value[`${activeId}-unConfig`]}>
              <>
                {t('knowledge.item.unconfiged')}
                (<UnConfigSkuCount />)
              </>
            </Badge>
          }
          key="notyet"
        >
          <div className="h-full flex flex-col overflow-hidden">
            <FilterBarValueProvider>
              <SpuSelect>
                <UnConfigFooterPortal drawer={drawer} knowledgeName={knowledgeName} />
              </SpuSelect>
            </FilterBarValueProvider>
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <Badge dot={value[`${activeId}-config`]}>
              <>
                {t('knowledge.已配置的SKU')}
                (<ConfigSkuCount />)
              </>
            </Badge>
          }
          key="yet"
        >
          <div className="h-full flex flex-col overflow-hidden">
            <FilterBarValueProvider>
              <SpuPreview>
                <ConfigFooterPortal citeMode={citeMode} drawer={drawer} />
              </SpuPreview>
            </FilterBarValueProvider>
          </div>
        </Tabs.TabPane>
      </Tabs>
    );
  },
);

SpuAreaInner.displayName = 'SpuAreaInner';

export default SpuArea;
